// Layer 4 (UI) — the Training compliance tab (locked comp
// `.impeccable/mocks/decision/documents.html` B): the sign-off matrix in the
// 8 columns, "Who signs what" in the 4. Where the row is too narrow for nine
// document columns, the roster takes the 8 with its open documents as
// references (the comp's C fallback). Renders `TrainingCompliance.transform.ts`;
// derives nothing.

import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { ActorChip } from '../advisory/PipelineStrip';
import { CARD_TABLE, DowntimeCard } from '../downtime/DowntimeCard';
import { ComplianceMeter, SignoffMatrix } from './SignoffMatrix';
import { CAPS, REF, REF_BAD } from './documents-ui';
import {
  openSignoffCount,
  openSignoffGroups,
  pendingPublishEffects,
  signoffMatrix,
  type OpenSignoffGroup,
  type PublishEffect,
  type SignoffMatrixRow,
} from './TrainingCompliance.transform';

export type TrainingComplianceProps = {
  /** A matrix column header opens that document on the Procedure library tab. */
  onSelectDocument: (id: string) => void;
};

export function TrainingCompliance({ onSelectDocument }: TrainingComplianceProps) {
  const matrix = signoffMatrix();
  const groups = openSignoffGroups();
  const effects = pendingPublishEffects();

  return (
    <Commentable name="TrainingCompliance">
      <div className="@container" data-training-compliance>
        <div className="grid gap-3.5 @[900px]:grid-cols-12 @[900px]:items-stretch">
          <div className="min-w-0 @[900px]:col-span-8">
            <div className="hidden h-full @[1085px]:block">
              <SignoffMatrix matrix={matrix} onSelectDocument={onSelectDocument} />
            </div>
            <div className="h-full @[1085px]:hidden">
              <OpenRoster rows={matrix.rows} />
            </div>
          </div>
          <div className="min-w-0 @[900px]:col-span-4">
            <WhoSignsWhat groups={groups} effects={effects} open={openSignoffCount(matrix)} />
          </div>
        </div>
      </div>
    </Commentable>
  );
}

/** The roster where the matrix cannot fit (comp C): Paul's columns, the open documents as references. */
function OpenRoster({ rows }: { rows: readonly SignoffMatrixRow[] }) {
  return (
    <DowntimeCard
      title="Roster"
      meta="marker = 90% target"
      className="h-full px-0 pb-0 pt-3 [&>header]:px-4"
      aria-label="Roster"
    >
      <div
        data-open-roster
        className={cn(
          CARD_TABLE,
          'border-t border-[var(--hairline)] [&_td:first-child]:pl-4 [&_th:first-child]:pl-4 [&_td:last-child]:pr-4 [&_th:last-child]:pr-4',
        )}
      >
        <Table wrap={false} className="tabular-nums">
          <THead>
            <TR>
              <TH>Name</TH>
              <TH>Role</TH>
              <TH className="text-center">Panel</TH>
              <TH className="w-[7.25rem]">Compliance %</TH>
              <TH className="w-16 !whitespace-normal text-right leading-tight">Overdue items</TH>
              <TH className="w-[11.5rem]">Open</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((row) => (
              <TR key={row.personId}>
                <TD className="whitespace-nowrap font-medium !text-ink-primary">{row.name}</TD>
                <TD className="!whitespace-normal leading-snug">{row.role}</TD>
                <TD className="text-center">{row.panel}</TD>
                <TD>
                  <ComplianceMeter percent={row.compliancePercent} tone={row.complianceTone} />
                </TD>
                <TD className="text-right font-semibold !text-ink-primary">{row.overdueItems}</TD>
                <TD>
                  <span className="flex flex-wrap gap-1">
                    {row.open.map((item) =>
                      item.state === 'stale' ? (
                        <span key={item.documentId} className={REF_BAD} title={`Stale · ${item.documentId} ${item.signedVersion}`}>
                          {item.documentId} {item.signedVersion}
                        </span>
                      ) : item.state === 'overdue' ? (
                        <span key={item.documentId} className={REF_BAD} title={`Missing · ${item.documentId}`}>
                          {item.documentId}
                        </span>
                      ) : (
                        <span key={item.documentId} className={REF} title={`Unsigned · ${item.documentId} · due ${item.dueLabel}`}>
                          {item.documentId} · {item.dueLabel}
                        </span>
                      ),
                    )}
                  </span>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </DowntimeCard>
  );
}

/** "Who signs what" (comp `.og`): every open sign-off grouped by why it is open, each person a Human chip. */
function WhoSignsWhat({
  groups,
  effects,
  open,
}: {
  groups: readonly OpenSignoffGroup[];
  effects: readonly PublishEffect[];
  open: number;
}) {
  return (
    <Commentable name="WhoSignsWhat">
      <DowntimeCard
        title="Who signs what"
        meta={
          <>
            <span data-open-signoffs className="font-semibold text-ink-primary">{open}</span> open
          </>
        }
        className="flex h-full flex-col px-0 pb-0 pt-3 [&>header]:mb-0 [&>header]:border-b [&>header]:border-[var(--hairline)] [&>header]:px-4 [&>header]:pb-2.5"
      >
        <div className="flex-1" data-who-signs-what>
          {groups.map((group) => (
            <div key={group.id} data-open-group={group.id}>
              <div className={cn(CAPS, 'flex items-baseline gap-2 border-b border-[var(--color-divider)] px-4 pb-1.5 pt-2.5')}>
                <span className="min-w-0 truncate">{group.label}</span>
                <span
                  data-open-group-count
                  className={cn(
                    'ml-auto text-[13px] font-semibold normal-case tracking-normal',
                    group.tone === 'bad' ? 'text-[var(--state-blocked)]' : 'text-ink-primary',
                  )}
                >
                  {group.count}
                </span>
              </div>
              {group.people.map((person) => (
                <div
                  key={person.personId}
                  data-open-person={person.personId}
                  className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border-b border-[var(--color-divider)] px-4 py-1.5"
                >
                  <span className="min-w-0 justify-self-start">
                    <ActorChip actor="Human">{`${person.name} · ${person.panel}`}</ActorChip>
                  </span>
                  <span className="flex flex-wrap justify-end gap-1">
                    {person.refs.map((ref) => (
                      <span key={ref.documentId} className={ref.tone === 'bad' ? REF_BAD : REF}>
                        {ref.label}
                      </span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
        {effects.map((effect) => (
          <div
            key={effect.documentId}
            data-publish-effect={effect.documentId}
            className="mt-auto flex flex-wrap items-center gap-2 border-t border-dashed border-[var(--hairline-strong)] px-4 pb-2.5 pt-2 text-xs text-ink-secondary"
          >
            <span className={CAPS}>
              On {effect.documentId} {effect.nextVersion} publish
            </span>
            <span className="font-semibold text-ink-primary">+{effect.resign.length}</span>
            {effect.resign.map((person) => (
              <ActorChip key={person.personId} actor="Human">
                {person.name}
              </ActorChip>
            ))}
          </div>
        ))}
      </DowntimeCard>
    </Commentable>
  );
}
